import * as THREE from 'three';
import { CONFIG } from '../../config.js';
import { getSharedBinaryAtlas } from '../../rendering/binary/sharedBinaryResources.js';
import { BinaryMaterial } from '../../rendering/binary/BinaryMaterial.js';
import { InteriorTemplate } from './InteriorTemplate.js';
import { pickInteriorDesignIndex } from './interiorDesigns.js';
import { interiorWorldOrigin, exteriorExitOffset } from './interiorOrigin.js';

/**
 * Portal flow: exterior door ↔ procedural interior cell.
 */
export class InteriorManager {
  /** @param {{ config?: typeof CONFIG.interiors }} [options] */
  constructor({ config = CONFIG.interiors } = {}) {
    this.config = config;
    this.group = new THREE.Group();
    this.group.name = 'Interiors';

    this._buildingMaterial = null;
    this._accentMaterial = null;
    /** @type {InteriorTemplate | null} */
    this._template = null;
    /** @type {import('./BuildingEntranceRegistry.js').BuildingEntranceRecord | null} */
    this._entrance = null;
    this._exteriorReturn = null;
    this._transition = null;
    this._cellIdBase = 2_400_000;
    /** @type {string | null} */
    this._activeDesignName = null;
  }

  get activeDesignName() {
    return this._activeDesignName;
  }

  get isInside() {
    return this._entrance !== null && !this._transition;
  }

  get isTransitioning() {
    return this._transition !== null;
  }

  /** @param {THREE.Group} worldRoot */
  attachToWorld(worldRoot) {
    worldRoot.add(this.group);
    if (!this._buildingMaterial) {
      const atlas = getSharedBinaryAtlas();
      this._buildingMaterial = new BinaryMaterial({
        atlas,
        palette: 'building',
      });
      this._accentMaterial = new BinaryMaterial({
        atlas,
        palette: 'accent',
        overrides: {
          glowStrength: CONFIG.luminance.accentGlowStrength,
          flickerStrength: 0.1,
        },
      });
    }
  }

  /**
   * Chunk streaming anchor while inside (keeps city loaded at the door).
   * @param {{ x: number; y: number; z: number }} playerPosition
   */
  getStreamingPosition(playerPosition) {
    if (this._exteriorReturn && (this.isInside || this._transition)) {
      return { x: this._exteriorReturn.x, z: this._exteriorReturn.z };
    }
    return { x: playerPosition.x, z: playerPosition.z };
  }

  /**
   * @param {{ x: number; z: number }} [_nearPosition]
   * @returns {import('../Colliders.js').BoxCollider[]}
   */
  getColliders(_nearPosition) {
    if (!this.isInside || !this._template) return [];
    return this._template.getWorldColliders();
  }

  /**
   * @param {number} delta
   * @param {{
   *   player: import('../../player/Player.js').Player;
   *   registry: import('./BuildingEntranceRegistry.js').BuildingEntranceRegistry;
   *   consumeInteract: () => boolean;
   *   onEnter?: () => void;
   *   onExit?: () => void;
   *   onTransition?: (opacity: number) => void;
   *   findExteriorGround: (x: number, z: number) => number;
   * }} ctx
   */
  update(delta, ctx) {
    if (!this.config.enabled) return;

    if (this._transition) {
      this._transition.elapsed += delta;
      const t = Math.min(1, this._transition.elapsed / this.config.fadeSeconds);
      const opacity = this._transition.phase === 'out' ? t : 1 - t;
      ctx.onTransition?.(opacity);
      if (t >= 1) {
        if (this._transition.phase === 'out') {
          this._finishEnter(ctx);
        } else {
          this._finishExit(ctx);
        }
        this._transition = null;
        ctx.onTransition?.(0);
      }
      return;
    }

    const { player, registry, consumeInteract } = ctx;
    const px = player.position.x;
    const pz = player.position.z;

    if (this.isInside && this._template && this._entrance) {
      const ex = this._template.exitPosition.x;
      const ez = this._template.exitPosition.z;
      const dist = Math.hypot(px - ex, pz - ez);
      if (dist <= this.config.exitRadius && consumeInteract()) {
        this._beginExit(ctx);
      }
      return;
    }

    const near = registry.findNearest(px, pz, this.config.enterRadius);
    if (near && consumeInteract()) {
      this._beginEnter(near, ctx);
    }
  }

  /**
   * @param {import('./BuildingEntranceRegistry.js').BuildingEntranceRegistry} registry
   * @param {{ x: number; z: number }} playerXZ
   */
  getInteractionPrompt(registry, playerXZ) {
    if (!this.config.enabled || this._transition) return null;
    if (this.isInside && this._template) {
      const ex = this._template.exitPosition.x;
      const ez = this._template.exitPosition.z;
      if (Math.hypot(playerXZ.x - ex, playerXZ.z - ez) <= this.config.exitRadius) {
        return '[E] EXIT';
      }
      return null;
    }
    const near = registry.findNearest(
      playerXZ.x,
      playerXZ.z,
      this.config.enterRadius,
    );
    if (near) return '[E] ENTER BUILDING';
    return null;
  }

  _beginEnter(entrance, ctx) {
    this._entrance = entrance;
    this._exteriorReturn = {
      x: ctx.player.position.x,
      y: ctx.player.position.y,
      z: ctx.player.position.z,
      yaw: ctx.player.look.yaw,
    };
    this._transition = { phase: 'out', elapsed: 0 };
    ctx.player.setMovementEnabled(false);
  }

  _beginExit(ctx) {
    this._transition = { phase: 'in', elapsed: 0 };
    ctx.player.setMovementEnabled(false);
  }

  _finishEnter(ctx) {
    const entrance = this._entrance;
    if (!entrance) return;

    this._disposeTemplate();
    const origin = interiorWorldOrigin(entrance.id, this.config);
    const designIndex =
      entrance.designIndex ??
      pickInteriorDesignIndex(entrance.interiorSeed ?? 0);
    this._template = new InteriorTemplate({
      designIndex,
      buildingMaterial: this._buildingMaterial,
      accentMaterial: this._accentMaterial,
      seed: entrance.interiorSeed,
      cellIdBase: this._cellIdBase,
      origin,
    });
    this._activeDesignName = this._template.designName;
    this.group.add(this._template.group);

    const spawn = this._template.spawnPosition;
    ctx.player.position.set(spawn.x, spawn.y, spawn.z);
    ctx.player.velocity.set(0, 0, 0);
    ctx.player.onGround = true;
    ctx.player.look.yaw = Math.PI;
    ctx.player.cameraRig.syncLook(ctx.player.look.yaw, ctx.player.look.pitch);
    ctx.player.setMovementEnabled(true);
    ctx.onEnter?.();
  }

  _finishExit(ctx) {
    const entrance = this._entrance;
    const saved = this._exteriorReturn;
    if (!entrance || !saved) return;

    const off = exteriorExitOffset(entrance.doorRotY, this.config.exteriorSpawnDistance);
    const x = entrance.doorX + off.x;
    const z = entrance.doorZ + off.z;
    const y = ctx.findExteriorGround(x, z);

    ctx.player.position.set(x, y, z);
    ctx.player.velocity.set(0, 0, 0);
    ctx.player.onGround = true;
    ctx.player.look.yaw = saved.yaw;
    ctx.player.cameraRig.syncLook(saved.yaw, ctx.player.look.pitch);
    ctx.player.setMovementEnabled(true);

    this._disposeTemplate();
    this._entrance = null;
    this._exteriorReturn = null;
    this._activeDesignName = null;
    ctx.onExit?.();
  }

  _disposeTemplate() {
    this._template?.dispose();
    this._template = null;
    this.group.clear();
  }

  updateMaterials(delta) {
    if (!CONFIG.graphics?.staticBinaryGlyphs) {
      this._buildingMaterial?.update(delta);
      this._accentMaterial?.update(delta);
    }
  }

  dispose() {
    this._disposeTemplate();
    this._buildingMaterial?.dispose();
    this._accentMaterial?.dispose();
    this._buildingMaterial = null;
    this._accentMaterial = null;
    this.group.clear();
  }
}
